/**
 * Contextual "thinking" messages while the AI prepares a Travel Dossier.
 *
 * Voice: an experienced travel consultant thinking out loud FOR the traveller.
 * First-person, warm, casual, never system-y. Every line reads like something
 * a real concierge would say ("Let me…", "I'm scouting…", "One moment —")
 * rather than a status update ("Loading hotels…").
 *
 * If a persona is not selected, we fall back to a general concierge script.
 */

const MESSAGES = {
  ms: {
    family: [
      "Biar saya cari hotel yang mesra kanak-kanak dulu…",
      "Saya nak pastikan jadualnya tak terlalu padat untuk anak-anak…",
      "Sedang scout tempat bermain yang selamat dan aktiviti edukatif…",
      "Saya pilih restoran yang mesra keluarga untuk anda…",
      "Sekejap ya, Travel Dossier keluarga anda hampir siap…",
    ],
    honeymoon: [
      "Biar saya pilih hotel butik yang paling romantik dulu…",
      "Saya nak cari spot sunset yang paling cantik untuk anda berdua…",
      "Sedang tempahkan restoran fine-dining untuk suasana malam…",
      "Saya susun sekali pengalaman berpasangan — spa & private tour…",
      "Sekejap, Travel Dossier bulan madu anda hampir siap…",
    ],
    riders: [
      "Biar saya susun laluan Kembara yang indah dan berbaloi…",
      "Sedang semak jarak dan keadaan jalan bagi setiap etap…",
      "Saya cari penginapan mesra Kembara dengan ruang parkir yang selamat…",
      "Sedang tandakan stesen minyak dan bengkel di sepanjang laluan…",
      "Sekejap ya, Travel Dossier Kembara anda hampir siap…",
    ],
    food: [
      "Biar saya susun food trail yang best dulu…",
      "Saya nak cari street food yang orang tempatan pun suka…",
      "Sedang semak status halal di mana boleh…",
      "Saya susun restoran mesti-cuba mengikut zon supaya senang gerak…",
      "Sekejap, Travel Dossier kulinari anda hampir siap…",
    ],
    shopping: [
      "Biar saya senaraikan outlet dan mall utama dulu…",
      "Saya semak sekali waktu sale supaya anda dapat harga terbaik…",
      "Sedang cari pasar tempatan untuk oleh-oleh yang lebih unik…",
      "Saya susun laluan shopping yang paling efisien untuk anda…",
      "Sekejap ya, Travel Dossier shopping anda hampir siap…",
    ],
    beach: [
      "Biar saya cari resort tepi pantai yang paling relax dulu…",
      "Saya nak scout spot snorkel dan pulau yang tak terlalu ramai…",
      "Sedang susun aktiviti air yang selamat dan seronok…",
      "Saya tandakan restoran seafood segar tepi pantai…",
      "Sekejap, Travel Dossier pantai anda hampir siap…",
    ],
    nature: [
      "Biar saya tengok taman negara dan air terjun dulu…",
      "Saya susun laluan hiking yang sesuai dengan tahap anda…",
      "Sedang cari eco-lodge yang tersorok dalam alam…",
      "Saya semak sekali cuaca dan keselamatan denai…",
      "Sekejap ya, Travel Dossier alam anda hampir siap…",
    ],
    themepark: [
      "Biar saya cari hotel dalam atau paling dekat dengan park dulu…",
      "Saya susun hari mengikut park yang anda pilih…",
      "Sedang bandingkan tiket express-pass yang berbaloi…",
      "Saya tandakan food court dalam park supaya senang cari…",
      "Sekejap, Travel Dossier theme park anda hampir siap…",
    ],
    default: [
      "Sekejap ya, biar saya dengar apa yang anda perlukan…",
      "Saya sedang fikir destinasi yang paling sesuai untuk anda…",
      "Sedang susun jadual harian yang seimbang…",
      "Saya kira anggaran bajet supaya realistik…",
      "Sekejap, Travel Dossier anda hampir siap…",
    ],
  },
  en: {
    family: [
      "Let me find hotels that welcome the kids first…",
      "I want the schedule to feel relaxed, not rushed…",
      "Scouting safe playgrounds and hands-on activities…",
      "Picking restaurants that are easy with children…",
      "One moment — your family Travel Dossier is almost ready…",
    ],
    honeymoon: [
      "Let me pick a boutique hotel that feels special for the two of you…",
      "I'm scouting the loveliest sunset spot for you…",
      "Booking a fine-dining moment for a memorable evening…",
      "Lining up couple experiences — spa & a private tour…",
      "One moment — your honeymoon Travel Dossier is almost ready…",
    ],
    riders: [
      "Let me map a scenic Touring route that's worth the journey…",
      "Checking distances and road conditions for each leg…",
      "Finding Touring-friendly stays with secure parking…",
      "Marking fuel stops and workshops along the way…",
      "One moment — your Touring Travel Dossier is almost ready…",
    ],
    food: [
      "Let me curate the food trail first…",
      "I'm hunting down street food the locals actually love…",
      "Verifying halal status where I can…",
      "Sorting must-try spots by district so you travel light…",
      "One moment — your culinary Travel Dossier is almost ready…",
    ],
    shopping: [
      "Let me line up the outlets and flagship malls first…",
      "Checking which sales are on so you catch the best prices…",
      "Finding local markets for more authentic souvenirs…",
      "Optimising the shopping route so you never backtrack…",
      "One moment — your shopping Travel Dossier is almost ready…",
    ],
    beach: [
      "Let me find you a resort right on the sand first…",
      "Scouting snorkel spots and quieter islands…",
      "Lining up water activities that are safe and fun…",
      "Marking fresh seafood places by the beach…",
      "One moment — your beach Travel Dossier is almost ready…",
    ],
    nature: [
      "Let me look at the national parks and waterfalls first…",
      "Tailoring a hiking route that fits your pace…",
      "Finding eco-lodges tucked deep in nature…",
      "Checking weather and trail safety for you…",
      "One moment — your nature Travel Dossier is almost ready…",
    ],
    themepark: [
      "Let me find a stay inside or right by the park first…",
      "Planning the days around the parks you picked…",
      "Comparing express-pass tickets to see what's worth it…",
      "Marking in-park dining so you don't waste time hunting food…",
      "One moment — your theme-park Travel Dossier is almost ready…",
    ],
    default: [
      "One moment — let me listen to what you need…",
      "I'm thinking through the destinations that fit you best…",
      "Sketching a balanced day-by-day plan…",
      "Doing the maths on a realistic budget…",
      "One moment — your Travel Dossier is almost ready…",
    ],
  },
};

export function getThinkingMessages(persona, lang = "ms") {
  const bucket = MESSAGES[lang] || MESSAGES.ms;
  return bucket[persona] || bucket.default;
}


